import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Copy, Trophy, MapPin, Instagram, Music, Twitter, Youtube, Facebook, Globe,
  Share2, Lock, Award, Clock, Filter, Gauge, BookOpen, Search,
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import SEO from "@/components/SEO";
import ProfileBanner from "@/components/ProfileBanner";
import { track, EVENT } from "@/lib/analytics";
import { FutureVisionPreview, FounderClosing } from "@/components/collector/CollectionLaunchExperience";

const SOCIAL_META = {
  instagram: { Icon: Instagram, label: "Instagram" },
  tiktok:    { Icon: Music,     label: "TikTok" },
  twitter:   { Icon: Twitter,   label: "X" },
  youtube:   { Icon: Youtube,   label: "YouTube" },
  facebook:  { Icon: Facebook,  label: "Facebook" },
  website:   { Icon: Globe,     label: "Website" },
};

const TIER_STYLE = {
  legendary: "bg-[var(--panini-red)] text-white",
  milestone: "bg-[var(--panini-yellow)] text-[var(--ink)]",
  rare:      "bg-[var(--ink)] text-[var(--panini-yellow)]",
  core:      "bg-white text-[var(--ink)] border-2 border-[var(--ink)]",
};

export default function CollectorProfile() {
  const { username } = useParams();
  const [state, setState] = useState({ loading: true, private: false, profile: null });
  const [timeline, setTimeline] = useState([]);
  const [score, setScore] = useState(null);

  useEffect(() => {
    let alive = true;
    const u = encodeURIComponent((username || "").toLowerCase());
    track(EVENT.PUBLIC_PROFILE_VIEWED, { username });
    api.get(`/public/collectors/${u}`)
      .then((r) => { if (alive) setState({ loading: false, private: !!r.data.private, profile: r.data.profile }); })
      .catch(() => { if (alive) setState({ loading: false, private: true, profile: null }); });
    api.get(`/public/collectors/${u}/timeline`)
      .then((r) => { if (alive && r.data.ok && !r.data.private) setTimeline(r.data.events || []); })
      .catch(() => {});
    api.get(`/public/collectors/${u}/score`)
      .then((r) => { if (alive && r.data.ok && !r.data.private) setScore(r.data.score); })
      .catch(() => {});
    return () => { alive = false; };
  }, [username]);

  if (state.loading) {
    return <div className="container-gm py-16 text-center text-[var(--smoke)]" data-testid="profile-loading">Loading…</div>;
  }

  if (state.private || !state.profile) {
    return <PrivateProfileScreen username={username} />;
  }

  const p = state.profile;
  const shareUrl = `${window.location.origin}/c/${p.username}`;
  const avatarUrl = p.avatar_media_id
    ? `${API}/public/collectors/${p.username}/avatar?v=${p.avatar_updated_at || ""}`
    : null;
  // Iter QA #007 — surface the profile banner on this route too so both
  // public profile URLs (`/c/{username}` and `/collectors/{slug}`) render
  // the collector's banner identically via the shared ProfileBanner.
  const bannerUrl = p.banner_media_id
    ? `${API}/public/collectors/${p.username}/banner?v=${encodeURIComponent(p.banner_updated_at || "")}`
    : null;

  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ url: shareUrl, title: p.display_name });
      else { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied."); }
    } catch { toast.error("Couldn't share."); }
  };

  return (
    <div className="bg-[var(--cream)] min-h-[calc(100vh-140px)]" data-testid="collector-profile-page">
      <SEO
        title={`${p.display_name} · ${p.public_collector_id} · Collector Registry`}
        description={p.bio || `${p.display_name} is a Gil Maakaroun Collector Registry member.`}
        image={avatarUrl || undefined}
        url={shareUrl}
      />

      {/* Iter QA #007 — shared ProfileBanner so both public profile routes
          render the collector's banner identically (object-contain, centered,
          complete artwork, blurred backdrop letterbox). */}
      <ProfileBanner src={bannerUrl} testid="collector-profile-banner" />

      {/* Hero */}
      <div className="border-b-2 border-[var(--ink)] bg-white">
        <div className="container-gm py-10">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] grid place-items-center shadow-[4px_4px_0_var(--ink)]">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="w-full h-full object-cover" data-testid="profile-avatar-img" />
              ) : (
                <span className="font-display text-3xl text-[var(--ink)]" data-testid="profile-avatar-initials">
                  {(p.display_name || "?").charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="kicker kicker-red">Collector Registry</div>
              <h1 className="h-display text-3xl md:text-4xl mt-1 truncate" data-testid="profile-display">{p.display_name}</h1>
              <div className="mt-2 flex flex-wrap gap-3 items-center text-sm text-[var(--smoke)]">
                <span data-testid="profile-username">@{p.username}</span>
                {p.public_collector_id && (
                  <PublicIdChip id={p.public_collector_id} />
                )}
                {p.country && (
                  <span className="inline-flex items-center gap-1" data-testid="profile-country">
                    <MapPin size={13} /> {p.country}
                  </span>
                )}
                {p.member_since && (
                  <span data-testid="profile-since">
                    Member since {new Date(p.member_since).toLocaleDateString(undefined, { year: "numeric", month: "short" })}
                  </span>
                )}
              </div>
              {p.bio && (
                <p className="mt-4 max-w-2xl text-[var(--ink)] whitespace-pre-wrap" data-testid="profile-bio">{p.bio}</p>
              )}
            </div>
            <button onClick={share} data-testid="profile-share" className="btn-red text-sm shrink-0">
              <Share2 size={16} /> Share
            </button>
          </div>

          {/* Social */}
          {Object.keys(p.social_urls || {}).length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2" data-testid="profile-social">
              {Object.entries(p.social_urls).map(([k, url]) => {
                const meta = SOCIAL_META[k];
                if (!meta || !url) return null;
                const Icon = meta.Icon;
                return (
                  <a
                    key={k}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    data-testid={`profile-social-${k}`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border-2 border-[var(--ink)] bg-white text-xs font-bold hover:bg-[var(--panini-yellow-soft)]"
                  >
                    <Icon size={13} /> {meta.label}
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="container-gm py-10 max-w-5xl space-y-10">
        {/* Iter QA #003 — Achievements moved above statistics so the
            collector's identity is emphasised before their numbers. */}
        {p.achievements && p.achievements.length > 0 && <AchievementsGrid list={p.achievements} />}
        {p.stats && <StatsGrid stats={p.stats} />}
        {score && <CollectionScore score={score} />}
        <StickerAlbumShowcase username={p.username} />
        {p.privacy?.show_certificates && (
          <TrophyCaseGallery username={p.username} initial={p.certificates || []} />
        )}
        {timeline && timeline.length > 0 && <TimelineSection events={timeline} />}

        {/* Operation WOW — Future Vision Preview + Founder Closing on the public profile.
            No functionality is claimed; every card is clearly marked "In Development". */}
        <div data-testid="profile-future-vision" className="mt-8">
          <FutureVisionPreview />
        </div>
        <div data-testid="profile-founder-closing" className="mt-6">
          <FounderClosing name={null} publicVariant />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// Phase 1E.6 — Public Album Showcase (sticker albums)
// ---------------------------------------------------------------
function StickerAlbumShowcase({ username }) {
  const [state, setState] = useState({ loading: true, data: null });
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("recent");

  useEffect(() => {
    let alive = true;
    setState({ loading: true, data: null });
    api.get(`/public/collectors/${encodeURIComponent(username)}/albums`)
      .then((r) => { if (alive) setState({ loading: false, data: r.data }); })
      .catch(() => { if (alive) setState({ loading: false, data: null }); });
    return () => { alive = false; };
  }, [username]);

  const filteredSorted = useMemo(() => {
    const items = state.data?.albums || [];
    const q = query.trim().toLowerCase();
    const search = (a) => !q || (a.search_text || "").includes(q);
    const bucket = (a) => {
      const pct = a?.completion?.percentage ?? 0;
      if (pct >= 100) return "completed";
      if (pct > 0)    return "in_progress";
      return "not_started";
    };
    const filterFn = (a) => filter === "all" || bucket(a) === filter;
    const arr = items.filter((a) => search(a) && filterFn(a));
    const sorter = {
      recent:  (a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""),
      highest: (a, b) => (b.completion?.percentage ?? 0) - (a.completion?.percentage ?? 0),
      lowest:  (a, b) => (a.completion?.percentage ?? 0) - (b.completion?.percentage ?? 0),
      name:    (a, b) => albumTitle(a).localeCompare(albumTitle(b)),
    }[sort];
    return [...arr].sort(sorter);
  }, [state.data, query, filter, sort]);

  if (state.loading) {
    return (
      <section data-testid="sticker-showcase-loading" className="text-[var(--smoke)] text-sm">
        Loading albums…
      </section>
    );
  }
  if (!state.data) return null;

  const { stats } = state.data;
  const hasAny = (state.data.albums || []).length > 0;

  return (
    <section data-testid="sticker-showcase">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-display text-2xl inline-flex items-center gap-2">
          <BookOpen size={22} className="text-[var(--panini-red)]" /> Public albums
        </h2>
        {hasAny && (
          <div className="text-[11px] font-bold uppercase tracking-widest text-[var(--smoke)]"
                data-testid="sticker-showcase-count">
            {filteredSorted.length} of {state.data.albums.length}
          </div>
        )}
      </div>

      {/* Overall stats */}
      <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-6 shadow-[4px_4px_0_var(--ink)] mb-5"
            data-testid="sticker-showcase-stats">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatTile testid="sst-albums" label="Albums" value={stats.album_count} />
          <StatTile testid="sst-collected" label="Collected" value={stats.total_collected} />
          <StatTile testid="sst-required" label="Total" value={stats.total_required} />
          <StatTile
            testid="sst-percentage"
            label="Overall"
            value={`${stats.weighted_percentage}%`}
            accent
          />
        </div>
      </div>

      {!hasAny ? (
        <div className="rounded-2xl border-2 border-dashed border-[var(--ink)]/40 bg-white p-6 text-[var(--smoke)] text-center text-sm"
              data-testid="sticker-showcase-empty">
          No public albums yet — this collector hasn&apos;t shared any albums.
        </div>
      ) : (
        <>
          {/* Search + filter + sort */}
          <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 mb-4 flex flex-wrap items-center gap-3"
                data-testid="sticker-showcase-controls">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={14}
                       className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--smoke)]" />
              <input
                type="text"
                data-testid="sticker-search"
                aria-label="Search public albums"
                placeholder="Search by name, manufacturer, release…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-full border-2 border-[var(--ink)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--panini-red)]"
              />
            </div>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter albums">
              {[
                { v: "all",         label: "All" },
                { v: "completed",   label: "Completed" },
                { v: "in_progress", label: "In Progress" },
                { v: "not_started", label: "Not Started" },
              ].map((f) => (
                <button
                  key={f.v}
                  type="button"
                  data-testid={`sticker-filter-${f.v}`}
                  onClick={() => setFilter(f.v)}
                  className={`px-3 py-1.5 rounded-full border-2 text-xs font-bold uppercase tracking-widest transition ${
                    filter === f.v
                      ? "bg-[var(--panini-red)] text-white border-[var(--panini-red)]"
                      : "bg-white text-[var(--ink)] border-[var(--ink)] hover:bg-[var(--panini-yellow-soft)]"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <select
              data-testid="sticker-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              aria-label="Sort albums"
              className="rounded-full border-2 border-[var(--ink)] bg-white px-3 py-1.5 text-xs font-bold text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--panini-red)]"
            >
              <option value="recent">Recently Updated</option>
              <option value="highest">Highest Completion</option>
              <option value="lowest">Lowest Completion</option>
              <option value="name">Album Name (A–Z)</option>
            </select>
          </div>

          {filteredSorted.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-[var(--ink)]/40 bg-white p-6 text-[var(--smoke)] text-center text-sm"
                  data-testid="sticker-showcase-no-match">
              No albums match those filters.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                  data-testid="sticker-showcase-grid">
              {filteredSorted.map((a) => (
                <AlbumCard key={a.public_slug} username={username} album={a} />
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function albumTitle(a) {
  // Iter Album-Title-Swap — prefer server-computed labels; degrade to
  // catalog variant/release name (never nickname) so the primary line is
  // always the canonical catalog identity.
  return a?.album_title
    || a?.catalog?.album_variant?.name
    || a?.catalog?.release?.name
    || "Untitled album";
}

// Suppression logic mirrors /app/backend/album_title.py — see also
// /app/frontend/src/lib/albumTitle.js.
const _GENERIC_NICK = new Set([
  "", "album", "my album", "demo album", "new album",
  "untitled", "untitled album",
  "test", "testing", "test album", "collection", "my collection",
]);
function albumSubtitle(a, title) {
  if (a?.album_subtitle !== undefined) return a.album_subtitle || null;
  const n = (a?.nickname || "").trim();
  if (!n) return null;
  const key = n.toLowerCase();
  if (_GENERIC_NICK.has(key)) return null;
  if (key === (title || "").toLowerCase()) return null;
  return n;
}

function StatTile({ label, value, testid, accent }) {
  return (
    <div data-testid={testid}
          className={`rounded-xl border-2 border-[var(--ink)] p-3 ${accent ? "bg-[var(--panini-yellow-soft)]" : "bg-[var(--cream)]"}`}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--smoke)]">{label}</div>
      <div className={`text-2xl font-display leading-tight ${accent ? "text-[var(--panini-red)]" : "text-[var(--ink)]"}`}>
        {value}
      </div>
    </div>
  );
}

function AlbumCard({ username, album }) {
  const title = albumTitle(album);
  const subtitle = albumSubtitle(album, title);
  const pct = album?.completion?.percentage ?? 0;
  // Iter Catalog-Cover-Meta — backend publishes `cover_image_url` alongside
  // the metadata object. Fall back to raw string values for older payloads.
  const rawVariantCover = album?.catalog?.album_variant?.cover_image;
  const rawReleaseCover = album?.catalog?.release?.cover_image;
  const cover = album?.catalog?.album_variant?.cover_image_url
             || album?.catalog?.release?.cover_image_url
             || (typeof rawVariantCover === "string" ? rawVariantCover : null)
             || (typeof rawReleaseCover === "string" ? rawReleaseCover : null)
             || null;
  const isComplete = album?.completion?.is_complete;
  const isNear = album?.completion?.is_near_completion;

  return (
    <Link
      to={`/c/${username}/albums/${album.public_slug}`}
      data-testid={`album-card-${album.public_slug}`}
      className="group block rounded-2xl border-2 border-[var(--ink)] bg-white overflow-hidden hover:shadow-[4px_4px_0_var(--ink)] hover:-translate-x-0.5 hover:-translate-y-0.5 transition"
    >
      {/* Iter QA #005 refinement — portrait 3:4 frame so covers read as
          physical albums rather than banners. */}
      <div className="aspect-[3/4] bg-neutral-50 border-b-2 border-[var(--ink)] flex items-center justify-center overflow-hidden">
        {cover ? (
          <img src={cover} alt=""
               className="max-w-full max-h-full w-auto h-auto object-contain object-center" />
        ) : (
          <BookOpen size={52} className="text-[var(--ink)]/40" />
        )}
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-display text-lg text-[var(--ink)] truncate" data-testid="album-card-title">
              {title}
            </div>
            {subtitle && (
              <div className="text-sm italic text-[var(--smoke)] truncate mt-0.5"
                   data-testid="album-card-subtitle">
                &ldquo;{subtitle}&rdquo;
              </div>
            )}
            {album?.catalog?.manufacturer?.name && (
              <div className="text-xs text-[var(--smoke)] truncate mt-0.5">
                {album.catalog.manufacturer.name}
                {album?.catalog?.release?.year ? ` · ${album.catalog.release.year}` : ""}
              </div>
            )}
          </div>
          <StatusBadge status={album.status} isComplete={isComplete} />
        </div>
        {/* Progress bar */}
        <div className="pt-1">
          <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-widest text-[var(--smoke)]">
            <span>Completion</span>
            <span data-testid="album-card-pct"
                   className={isComplete ? "text-[var(--panini-red)]"
                              : isNear ? "text-[var(--panini-red)]" : "text-[var(--ink)]"}>
              {pct}%
            </span>
          </div>
          <div className="mt-1 h-2 rounded-full bg-[var(--cream)] border border-[var(--ink)] overflow-hidden">
            <div
              className={`h-full ${isComplete ? "bg-[var(--panini-red)]" : "bg-[var(--panini-yellow)]"}`}
              style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
            />
          </div>
          <div className="mt-1 text-[11px] text-[var(--smoke)]">
            {(album.completion.owned + album.completion.duplicate)} / {album.completion.total} stickers
          </div>
        </div>
      </div>
    </Link>
  );
}

function StatusBadge({ status, isComplete }) {
  if (isComplete) {
    return (
      <span data-testid="album-card-status"
             className="shrink-0 px-2 py-0.5 rounded-full bg-[var(--panini-red)] text-white text-[10px] font-bold uppercase tracking-widest">
        Complete
      </span>
    );
  }
  if (status === "completed") {
    return (
      <span data-testid="album-card-status"
             className="shrink-0 px-2 py-0.5 rounded-full bg-[var(--ink)] text-[var(--panini-yellow)] text-[10px] font-bold uppercase tracking-widest">
        Marked complete
      </span>
    );
  }
  return (
    <span data-testid="album-card-status"
           className="shrink-0 px-2 py-0.5 rounded-full bg-[var(--cream)] border-2 border-[var(--ink)] text-[var(--ink)] text-[10px] font-bold uppercase tracking-widest">
      In progress
    </span>
  );
}

// ---------------------------------------------------------------
// Collection Score card
// ---------------------------------------------------------------
function CollectionScore({ score }) {
  return (
    <section data-testid="profile-score">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-2xl inline-flex items-center gap-2">
          <Gauge size={22} className="text-[var(--panini-red)]" /> Collection score
        </h2>
        <div className="text-[11px] font-bold uppercase tracking-widest text-[var(--smoke)]">
          Activity signal · not a ranking
        </div>
      </div>
      <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-6 shadow-[4px_4px_0_var(--ink)]">
        <div className="flex items-end gap-3">
          <div className="font-display text-6xl text-[var(--panini-red)] leading-none" data-testid="score-total">
            {score.total}
          </div>
          <div className="text-sm text-[var(--smoke)] pb-2">points</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-5">
          {Object.entries(score.breakdown).map(([k, v]) => (
            <div key={k} data-testid={`score-breakdown-${k}`}
                  className="rounded-xl border-2 border-[var(--ink)] bg-[var(--cream)] p-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--smoke)]">
                {labelForKey(k)}
              </div>
              <div className="text-lg font-display text-[var(--ink)]">{v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function labelForKey(k) {
  return ({
    certificates: "Certificates",
    claims_approved: "Claims",
    achievements: "Achievements",
    loyalty_years: "Loyalty",
    verified_bonus: "Verified",
  }[k]) || k;
}

// ---------------------------------------------------------------
// Trophy Case + Gallery filters (Phase 5.2 & 5.4)
// ---------------------------------------------------------------
function TrophyCaseGallery({ username, initial }) {
  const [filters, setFilters] = useState({ album: "all", year: "all", achievement: "all" });
  const [rows, setRows] = useState(initial || []);
  const [facets, setFacets] = useState({ years: [], albums: [], achievements: [] });
  const [loading, setLoading] = useState(false);

  const applied = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.album !== "all") p.set("album", filters.album);
    if (filters.year !== "all") p.set("year", filters.year);
    if (filters.achievement !== "all") p.set("achievement", filters.achievement);
    return p.toString();
  }, [filters]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const qs = applied ? `?${applied}` : "";
    api.get(`/public/collectors/${encodeURIComponent(username)}/gallery${qs}`)
      .then((r) => {
        if (!alive) return;
        setRows(r.data.certificates || []);
        setFacets(r.data.facets || { years: [], albums: [], achievements: [] });
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [applied, username]);

  return (
    <section data-testid="profile-trophy-case">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-display text-2xl inline-flex items-center gap-2">
          <Award size={22} className="text-[var(--panini-red)]" /> Trophy case
        </h2>
        <div className="text-[11px] font-bold uppercase tracking-widest text-[var(--smoke)]">
          {rows.length} certificate{rows.length === 1 ? "" : "s"}
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="gallery-filters">
        <div className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-[var(--smoke)]">
          <Filter size={12} /> Filter
        </div>
        <FilterSelect
          testid="filter-album"
          value={filters.album}
          onChange={(v) => setFilters((f) => ({ ...f, album: v }))}
          options={[{ value: "all", label: "All albums" },
            ...(facets.albums || []).map((a) => ({ value: a.slug, label: `${a.name} (${a.count})` }))]}
        />
        <FilterSelect
          testid="filter-year"
          value={filters.year}
          onChange={(v) => setFilters((f) => ({ ...f, year: v }))}
          options={[{ value: "all", label: "All years" },
            ...(facets.years || []).map((y) => ({ value: String(y.year), label: `${y.year} (${y.count})` }))]}
        />
        <FilterSelect
          testid="filter-achievement"
          value={filters.achievement}
          onChange={(v) => setFilters((f) => ({ ...f, achievement: v }))}
          options={[{ value: "all", label: "Any achievement" },
            ...(facets.achievements || []).map((a) => ({ value: a, label: a.replace(/_/g, " ") }))]}
        />
      </div>

      {loading ? (
        <div className="text-[var(--smoke)] text-sm" data-testid="gallery-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-[var(--ink)]/40 bg-white p-6 text-[var(--smoke)] text-center text-sm" data-testid="gallery-empty">
          No certificates match those filters.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {rows.map((c) => (
            <Link
              key={c.cert_number}
              to={`/certificates/${c.cert_number}`}
              data-testid={`cert-${c.cert_number}`}
              className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 hover:shadow-[3px_3px_0_var(--ink)] hover:-translate-x-0.5 hover:-translate-y-0.5 transition"
            >
              <div className="font-mono text-[var(--panini-red)] font-bold text-sm">{c.cert_number}</div>
              <div className="text-xs text-[var(--smoke)] mt-1">{c.album || "—"}</div>
              {c.completion_year && (
                <div className="text-[11px] text-[var(--smoke)] mt-0.5">Season {c.completion_year}</div>
              )}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function FilterSelect({ testid, value, onChange, options }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-full border-2 border-[var(--ink)] bg-white px-3 py-1.5 text-xs font-bold text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--panini-red)]"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

// ---------------------------------------------------------------
// Timeline (Phase 5.3)
// ---------------------------------------------------------------
function TimelineSection({ events }) {
  return (
    <section data-testid="profile-timeline">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-2xl inline-flex items-center gap-2">
          <Clock size={22} className="text-[var(--panini-red)]" /> Timeline
        </h2>
        <div className="text-[11px] font-bold uppercase tracking-widest text-[var(--smoke)]">
          {events.length} event{events.length === 1 ? "" : "s"}
        </div>
      </div>
      <ol className="relative border-l-2 border-[var(--ink)] pl-6 space-y-4">
        {events.map((e, idx) => (
          <li key={idx} data-testid={`timeline-${idx}`} className="relative">
            <span className="absolute -left-[31px] top-1 w-4 h-4 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-yellow)]" />
            <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 shadow-[3px_3px_0_var(--ink)]">
              <div className="text-[11px] font-bold uppercase tracking-widest text-[var(--smoke)]">
                {e.at ? new Date(e.at).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric" }) : ""}
              </div>
              <div className="mt-1 font-bold text-[var(--ink)]">{e.label}</div>
              {e.type === "certificate_claimed" && e.cert_number && (
                <Link to={`/certificates/${e.cert_number}`} className="text-sm text-[var(--panini-red)] font-bold hover:underline">
                  {e.cert_number} →
                </Link>
              )}
              {e.type === "achievement" && e.badge_id && (
                <div className="text-xs text-[var(--smoke)]">{e.badge_id.replace(/_/g, " ")}</div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function StatsGrid({ stats }) {
  const rows = [
    { key: "certificates_owned", label: "Certificates" },
    { key: "certificates_approved_claims", label: "Claims approved" },
    { key: "achievements", label: "Achievements" },
    { key: "years_collecting", label: "Years collecting" },
  ];
  return (
    <section data-testid="profile-stats">
      <h2 className="font-display text-2xl mb-4">Stats</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {rows.map(({ key, label }) => (
          <div key={key} data-testid={`stat-${key}`}
                className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 shadow-[3px_3px_0_var(--ink)]">
            <div className="text-3xl font-display text-[var(--panini-red)]">{stats[key] ?? 0}</div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-[var(--smoke)] mt-1">{label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function AchievementsGrid({ list }) {
  return (
    <section data-testid="profile-achievements">
      <h2 className="font-display text-2xl mb-4">Achievements</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {list.map((a) => (
          <div key={a.badge_id} data-testid={`achievement-${a.badge_id}`}
                className={`rounded-2xl border-2 border-[var(--ink)] p-4 shadow-[3px_3px_0_var(--ink)] ${TIER_STYLE[a.tier] || TIER_STYLE.core}`}>
            <div className="flex items-center gap-2">
              <Trophy size={16} />
              <div className="font-bold text-sm">{a.label}</div>
            </div>
            <div className="text-xs mt-1 opacity-80">{a.description}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PublicIdChip({ id }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(id); toast.success("Public Collector ID copied."); }
    catch { toast.error("Couldn't copy."); }
  };
  return (
    <button type="button" onClick={copy} data-testid="profile-public-id"
             className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--panini-yellow-soft)] font-mono text-xs font-bold text-[var(--ink)] hover:bg-[var(--panini-yellow)]">
      {id} <Copy size={11} />
    </button>
  );
}

function PrivateProfileScreen({ username }) {
  return (
    <div className="container-gm py-16 max-w-lg" data-testid="profile-private-page">
      <SEO title="Private collector profile" />
      <div className="rounded-3xl border-2 border-[var(--ink)] bg-white p-8 text-center shadow-[6px_6px_0_var(--ink)]">
        <div className="mx-auto w-14 h-14 rounded-full bg-[var(--panini-yellow-soft)] border-2 border-[var(--ink)] grid place-items-center">
          <Lock size={22} className="text-[var(--ink)]" />
        </div>
        <div className="kicker kicker-black mt-6">Profile</div>
        <h1 className="h-display text-3xl mt-2">This profile is private.</h1>
        <p className="mt-3 text-[var(--smoke)]">
          @{username} either hasn&apos;t made their profile public yet or this
          username isn&apos;t recognised. Nothing more to see here.
        </p>
        <Link to="/collectors" className="btn-ghost text-sm mt-6" data-testid="private-back">
          Browse public collectors
        </Link>
      </div>
    </div>
  );
}
